import { useTranslation } from 'react-i18next';

import { Button } from '../../ui/button';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export type VehicleComparisonRow = {
  key: string;
  label: string;
  current?: string | null;
  proposed?: string | null;
};

type VehicleComparisonCardProps = {
  rows: Array<VehicleComparisonRow>;
  onKeepCurrent: () => void;
  onReplace: () => void;
  // Shown only after the broker tries "Suivant" without resolving the choice.
  showBlockedMessage?: boolean;
};

export const VehicleComparisonCard = ({
  rows,
  onKeepCurrent,
  onReplace,
  showBlockedMessage = false,
}: VehicleComparisonCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-4">
      <Group gap={2} className="mb-3">
        <Text
          inline
          size="xs"
          weight="medium"
          className="rounded-full bg-primary px-2 py-0.5 text-primary-foreground"
        >
          {t('vehicleInput.comparison.badge')}
        </Text>
        <Text inline size="sm" weight="medium">
          {t('vehicleInput.comparison.question')}
        </Text>
      </Group>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        {rows.map(row => {
          const changed = (row.current ?? '') !== (row.proposed ?? '');
          return (
            <Stack key={row.key} gap={0} className="gap-0.5">
              <dt className="text-xs text-muted-foreground">{row.label}</dt>
              <dd className="font-medium">
                {changed ? (
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    {row.current ? (
                      <Text inline tone="muted" className="line-through">
                        {row.current}
                      </Text>
                    ) : null}
                    <Text inline weight="semibold" tone="primary">
                      {row.proposed || '—'}
                    </Text>
                  </span>
                ) : (
                  row.current || '—'
                )}
              </dd>
            </Stack>
          );
        })}
      </dl>

      <Group justify="end" gap={3} className="mt-4">
        <button
          type="button"
          onClick={onKeepCurrent}
          className="text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('vehicleInput.comparison.keepCurrent')}
        </button>
        <Button type="button" onClick={onReplace}>
          {t('vehicleInput.comparison.replace')}
        </Button>
      </Group>

      {showBlockedMessage && (
        <Text size="xs" weight="medium" className="mt-2 text-right text-amber-700">
          {t('vehicleInput.comparison.blocked')}
        </Text>
      )}
    </div>
  );
};
